import React, { ReactNode, useCallback, useMemo } from 'react';

import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import ExpandMore from '@mui/icons-material/ExpandMore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ImagingStudyWorklistMapping from '@worklist-2/core/src/fhir/resource/columnMapping/ImagingStudyWorklistMapping';

// Interface for props
interface IDataGroupRowProps {
	tableRow: {
		getIsExpanded: () => boolean,
		toggleExpanded: () => void,
		id: string,
		original: {
			rawData: any,
			expandRow: (id: string, rawData: any) => void,
		},
		getVisibleCells: () => Array<any>,
	};
	expandRow: (id: string, rawData: any) => void;
	groupingItems: Array<any>;
	isRowLoading: boolean;
	collapseRow: (id: string) => void;
	expandedRows: Array<string>;
}

const defaultHeight = 36;
const defaultPaddingLeft = 34;
const firstLevelColor = 'rgba(42, 51, 56, 1)';
const secondLevelColor = 'rgba(41, 41, 41, 1)';

const DataGroupRow: React.FC<IDataGroupRowProps> = React.memo(
	({ tableRow, expandRow, groupingItems, isRowLoading, collapseRow, expandedRows }): ReactNode => {
		const levelNumber = tableRow.depth;

		const isExpanded = useMemo(() => expandedRows.includes(tableRow.id), [expandedRows]);

		const getRowContent = useCallback(() => {
			let contentRow = null;

			if (groupingItems[levelNumber]) {
				contentRow = ImagingStudyWorklistMapping[groupingItems[levelNumber].id].getGroupDisplay(tableRow);
			}

			return contentRow;
		}, [tableRow, groupingItems]);
		return (
			<TableRow>
				<TableCell
					colSpan={tableRow.getAllCells().length}
					sx={{
						height: defaultHeight,
						background: levelNumber > 0 ? secondLevelColor : firstLevelColor,
						padding: 0,
						borderBottom: '1px solid rgba(28, 28, 28, 1)',
						paddingLeft: `${defaultPaddingLeft * (levelNumber + 1)}px`,
					}}
				>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'flex-start',
							height: '100%',
							gap: '10px',
							color: 'rgba(255, 255, 255, 0.87)',
						}}
					>
						<IconButton
							data-testid={`expand-row-${tableRow.id}`}
							disabled={isRowLoading}
							sx={{
								background: 'rgba(43, 116, 174, 1)',
								width: 24,
								height: 24,
								border: '2px solid rgba(26, 26, 26, 1)',
								svg: {
									fontSize: 20,
								},
							}}
							onClick={() => {
								if (!isExpanded) {
									expandRow(tableRow.id, tableRow.original.rawData);
								} else {
									collapseRow(tableRow.id);
								}
								tableRow.toggleExpanded(!isExpanded);
							}}
						>
							{isExpanded ? <ExpandMore /> : <NavigateNextIcon />}
						</IconButton>
						{getRowContent()}
					</Box>
				</TableCell>
			</TableRow>
		);
	}
);

DataGroupRow.displayName = 'DataGroupRow';

export default DataGroupRow;
